import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { ShieldAlert, LogOut } from "lucide-react";
import { useState } from "react";

/**
 * Sticky red banner that appears on every page when an admin has used the
 * "Sign in as user" feature. Clicking "End session" restores the admin's
 * original session and reloads the page.
 */
export default function ImpersonationBanner() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);

  const imp = user?._impersonation;
  if (!imp?.active) return null;

  const end = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/admin/end-impersonation");
      toast.success("Restored admin session");
      // Hard reload so every component re-reads /auth/me with the admin cookie
      window.location.assign("/admin/users");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't end impersonation");
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-[100] bg-[#FF2A00] text-white border-b-2 border-black"
    >
      <div className="max-w-[1400px] mx-auto px-3 sm:px-6 py-2 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <ShieldAlert className="w-5 h-5 shrink-0" />
          <div className="text-[12px] sm:text-sm leading-snug min-w-0">
            <span className="font-bold uppercase tracking-widest text-[10px] sm:text-[11px]">
              Admin impersonation
            </span>
            <span className="block sm:inline sm:ml-2 truncate">
              Signed in as <b>{user.name}</b> ({user.email}). Original admin: <b>{imp.admin_email}</b>
            </span>
          </div>
        </div>
        <button
          data-testid="end-impersonation-btn"
          type="button"
          onClick={end}
          disabled={busy}
          className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 bg-white text-[#FF2A00] text-[11px] uppercase tracking-widest font-bold hover:bg-black hover:text-white border border-white hover:border-black transition-colors disabled:opacity-50"
        >
          <LogOut className="w-3.5 h-3.5" />
          {busy ? "Ending…" : "End session"}
        </button>
      </div>
    </div>
  );
}
